/**
 * Homestead placed-piece renderer — bakes each beacon's piece list into
 * InstancedMeshes (pieceInstances → bakePieceMeshes, the settlement bucket
 * conventions) parented to the owning planet's group, so builds ride orbit +
 * spin for free (the HomesteadMarkers precedent).
 *
 * Fed by the owner-only `hsState` snapshot (setAll) and live `hsDelta`
 * piece-add / piece-remove broadcasts (applyPieceDelta). A change REBAKES that
 * one beacon — piece budgets cap a beacon at ≤2500 pieces, so a rebake is a
 * few InstancedMesh builds, not a frame-budget problem; per-piece incremental
 * instancing can come later if profiling ever demands it.
 *
 * Also the client-side collision + demolish-pick seam: aabbsNear() surfaces
 * per-piece bounds for the character controller and the build mode.
 */
import * as THREE from "three";
import {
  PIECE_CATALOG,
  homesteadIndoorsAt,
  homesteadRoomAt,
  solveHomesteadRooms,
  type Beacon,
  type HomesteadRoom,
  type HomesteadRoomSolution,
  type PlacedPiece,
} from "@hyperlight/shared";
import { compiledPieces, pieceInstances, bakePieceMeshes } from "../homestead/meshes.js";
import { socketKind, type SnapSocket } from "../homestead/build.js";
import { catalogObbs, pieceWorldObbs, standRadiusObbs, resolveObbs, occludesObbs, segmentHitObbs, rayHitObbs, type WorldObb } from "../homestead/collision.js";
import type { StarSystem } from "./starsystem.js";

/** Demolish pick: every collision box is inflated this far, so a mathematical
 *  crosshair ray can still hit a 0.12 m railing rail. */
export const PICK_PAD_M = 0.14;
/** …and the previous pick keeps the reticle while it is within this of the new
 *  best (hysteresis on a shared edge — NOT a lock on the old target). */
export const PICK_STICKY_M = 0.3;

interface BeaconBuild {
  planetId: string;
  pieces: PlacedPiece[];
  group: THREE.Group; // parented to planet.group; instance matrices are planet-local
  /** Per-instance collision boxes (planet-local), rebuilt with the bake. */
  obbs: WorldObb[];
  /** Pure shared enclosure/function solve, rebuilt from the same piece list. */
  rooms: HomesteadRoomSolution;
  /** Coarse bound over the build (centroid + reach) — the collision prefilter. */
  boundC: [number, number, number];
  boundR: number;
  /** Full geometry baked (near) vs hologram proxy only (far / pending). */
  baked: boolean;
  /** One shared-geometry InstancedMesh of piece bounds — the pre-bake silhouette. */
  proxy: THREE.InstancedMesh | null;
  /** No promotion before this (ms) — the login/entry hologram dwell. */
  promoteAfter: number;
  /** While baked, the hologram lingers overlaid until this (ms; 0 = gone). */
  lingerUntil: number;
}

/** Camera closer than this to a build's bound → bake the real geometry… */
export const BAKE_NEAR_M = 280;
/** …and farther than this → demote back to the hologram (hysteresis). */
export const BAKE_FAR_M = 340;
/** Fresh snapshots (login / entering a system) HOLD the hologram this long
 *  before any promotion — the subtle materialize beat plays even when you
 *  spawn standing inside your own base. */
export const REVEAL_DWELL_MS = 900;
/** After the real bake lands, the hologram glow LINGERS overlaid this long
 *  (additive — reads as the construct materializing), then vanishes. */
export const REVEAL_LINGER_MS = 450;

export class HomesteadPieces {
  private builds = new Map<string, BeaconBuild>();
  // Hologram proxy: ONE unit box + ONE additive material shared by every build —
  // a whole distant homestead is a single draw call and zero geometry work.
  private proxyGeo = new THREE.BoxGeometry(1, 1, 1);
  private proxyMat = new THREE.MeshBasicMaterial({
    color: 0x2fb8df, transparent: true, opacity: 0.16, depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  constructor(private system: StarSystem) {}

  /** Builds currently carrying FULL geometry (probe/tuning readback). */
  get bakedCount(): number {
    let n = 0;
    for (const b of this.builds.values()) if (b.baked) n++;
    return n;
  }

  /**
   * Distance-staged baking, called once per render frame. `focusFor` returns
   * the camera in a planet's RENDER-local frame (or null when unresolvable).
   * At most `budget` full bakes per frame (spread the cost — no join hitch);
   * far builds demote back to the hologram and free their geometry.
   */
  update(focusFor: (planetId: string) => [number, number, number] | null, budget = 1,
    nowMs = performance.now()): void {
    for (const build of this.builds.values()) {
      if (build.pieces.length === 0) continue;
      // The materialize linger: hologram glow rides the fresh bake, then goes.
      if (build.baked && build.proxy && nowMs >= build.lingerUntil) {
        build.group.remove(build.proxy);
        build.proxy.dispose();
        build.proxy = null;
      }
      const focus = focusFor(build.planetId);
      const dist = focus === null ? Infinity : Math.sqrt(
        (focus[0] - build.boundC[0]) ** 2
        + (focus[1] - build.boundC[1]) ** 2
        + (focus[2] - build.boundC[2]) ** 2) - build.boundR;
      if (!build.baked && dist < BAKE_NEAR_M && budget > 0 && nowMs >= build.promoteAfter) {
        this.bakeFull(build, nowMs);
        budget--;
      } else if (build.baked && dist > BAKE_FAR_M) {
        this.demote(build);
      }
    }
  }

  /** Bake the real instanced geometry; the hologram lingers briefly on top. */
  private bakeFull(build: BeaconBuild, nowMs = 0): void {
    const { meshes } = bakePieceMeshes(pieceInstances(build.pieces));
    for (const m of meshes) build.group.add(m);
    build.baked = true;
    if (build.proxy && nowMs > 0) {
      build.lingerUntil = nowMs + REVEAL_LINGER_MS; // update() removes it after
    } else if (build.proxy) {
      build.group.remove(build.proxy);
      build.proxy.dispose();
      build.proxy = null;
    }
  }

  /** Back to the hologram; the real geometry is freed. */
  private demote(build: BeaconBuild): void {
    for (const child of [...build.group.children]) {
      build.group.remove(child);
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        const mat = child.material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else mat.dispose();
      }
    }
    build.proxy = this.makeProxy(build.pieces);
    if (build.proxy) build.group.add(build.proxy);
    build.baked = false;
    build.lingerUntil = 0;
  }

  /** Piece silhouettes: one instanced unit box per piece, scaled to its doc bounds. */
  private makeProxy(pieces: readonly PlacedPiece[]): THREE.InstancedMesh | null {
    if (pieces.length === 0) return null;
    const mesh = new THREE.InstancedMesh(this.proxyGeo, this.proxyMat, pieces.length);
    const mtx = new THREE.Matrix4();
    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    const centre = new THREE.Vector3();
    let n = 0;
    for (const piece of pieces) {
      const entry = PIECE_CATALOG[piece.t];
      const asset = entry ? compiledPieces().get(entry.id) : undefined;
      if (!asset) continue;
      const bb = asset.bounds;
      quat.set(piece.q[0], piece.q[1], piece.q[2], piece.q[3]);
      centre.set((bb.min[0] + bb.max[0]) / 2, (bb.min[1] + bb.max[1]) / 2, (bb.min[2] + bb.max[2]) / 2)
        .applyQuaternion(quat);
      pos.set(piece.p[0] + centre.x, piece.p[1] + centre.y, piece.p[2] + centre.z);
      scl.set(
        Math.max(0.1, bb.max[0] - bb.min[0]),
        Math.max(0.1, bb.max[1] - bb.min[1]),
        Math.max(0.1, bb.max[2] - bb.min[2]));
      mesh.setMatrixAt(n++, mtx.compose(pos, quat, scl));
    }
    mesh.count = n;
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
    return mesh;
  }

  /** Total pieces rendered (probe/debug readback). */
  get count(): number {
    let n = 0;
    for (const b of this.builds.values()) n += b.pieces.length;
    return n;
  }

  /** Replace everything from an authoritative hsState snapshot. */
  setAll(beacons: Beacon[], pieces: Record<string, PlacedPiece[]>): void {
    const byId = new Map(beacons.map((b) => [b.id, b]));
    // Drop builds whose beacon vanished.
    for (const id of [...this.builds.keys()]) if (!byId.has(id)) this.drop(id);
    for (const b of beacons) this.rebake(b.id, b.planetId, pieces[b.id] ?? []);
  }

  /** Apply a live piece delta. Unknown beacons are ignored (their hsState will
   *  land on the next system push; a beacon delta always precedes its pieces). */
  applyPieceDelta(op: "piece-add" | "piece-remove", beaconId: string,
    piece: PlacedPiece | null, pid?: number): void {
    const build = this.builds.get(beaconId);
    if (!build) return;
    const next = op === "piece-add" && piece
      ? [...build.pieces, piece]
      : build.pieces.filter((p) => p.pid !== pid);
    this.rebake(beaconId, build.planetId, next);
  }

  /** Register a beacon container (no pieces yet) so deltas have a home. */
  ensureBeacon(beacon: Beacon): void {
    if (!this.builds.has(beacon.id)) this.rebake(beacon.id, beacon.planetId, []);
  }

  /** A beacon vanished (hsDelta remove) — drop its build outright. */
  removeBeacon(beaconId: string): void {
    this.drop(beaconId);
  }

  /** Planet-local snap sockets of placed pieces near `p` (build-mode snap
   *  candidates), each carrying its KIND so the snap can refuse nonsense pairs
   *  (a corner welded to an edge midpoint is a half-unit offset, never intent).
   *  Piece proximity is pre-filtered by centre distance. */
  socketsNear(beaconId: string, p: [number, number, number], within: number): SnapSocket[] {
    const build = this.builds.get(beaconId);
    if (!build) return [];
    const compiled = compiledPieces();
    const out: SnapSocket[] = [];
    const reach2 = (within + 6) * (within + 6); // pieces are ≤ ~5 m across
    for (const piece of build.pieces) {
      const dx = piece.p[0] - p[0], dy = piece.p[1] - p[1], dz = piece.p[2] - p[2];
      if (dx * dx + dy * dy + dz * dz > reach2) continue;
      const entry = PIECE_CATALOG[piece.t];
      const asset = entry ? compiled.get(entry.id) : undefined;
      if (!asset) continue;
      for (const s of asset.sockets) {
        const world = rotP(piece.q, s.position[0], s.position[1], s.position[2]);
        out.push({
          p: [world[0] + piece.p[0], world[1] + piece.p[1], world[2] + piece.p[2]],
          k: socketKind(s.name),
        });
      }
    }
    return out;
  }

  /** The placed pieces of a beacon (copies — support-graph inputs). */
  piecesOf(beaconId: string): PlacedPiece[] {
    const build = this.builds.get(beaconId);
    return build ? build.pieces.map((p) => ({ ...p })) : [];
  }

  /** Doc bounds → planet-local sphere for one piece (rotation-safe radius). */
  private boundsOf(piece: PlacedPiece):
    { centre: [number, number, number]; radius: number; half: [number, number, number] } | null {
    const entry = PIECE_CATALOG[piece.t];
    const asset = entry ? compiledPieces().get(entry.id) : undefined;
    if (!asset) return null;
    const b = asset.bounds;
    const cx = (b.min[0] + b.max[0]) / 2, cy = (b.min[1] + b.max[1]) / 2, cz = (b.min[2] + b.max[2]) / 2;
    const centre = rotP(piece.q, cx, cy, cz);
    centre[0] += piece.p[0]; centre[1] += piece.p[1]; centre[2] += piece.p[2];
    const rx = (b.max[0] - b.min[0]) / 2, ry = (b.max[1] - b.min[1]) / 2, rz = (b.max[2] - b.min[2]) / 2;
    return { centre, radius: Math.sqrt(rx * rx + ry * ry + rz * rz), half: [rx, ry, rz] };
  }

  /** Per-piece world-ish info near a planet-local point (collision + demolish
   *  pick): pid + planet-local centre + a bounding radius from the compiled doc. */
  aabbsNear(beaconId: string, p: [number, number, number], within: number):
    { pid: number; centre: [number, number, number]; radius: number }[] {
    const build = this.builds.get(beaconId);
    if (!build) return [];
    const out: { pid: number; centre: [number, number, number]; radius: number }[] = [];
    for (const piece of build.pieces) {
      const b = this.boundsOf(piece);
      if (!b) continue;
      const dx = b.centre[0] - p[0], dy = b.centre[1] - p[1], dz = b.centre[2] - p[2];
      if (dx * dx + dy * dy + dz * dz <= (within + b.radius) * (within + b.radius)) {
        out.push({ pid: piece.pid, centre: b.centre, radius: b.radius });
      }
    }
    return out;
  }

  /**
   * The piece a planet-local ray enters FIRST — the reticle target for the
   * empty-hand demolish flow. `dir` must be unit.
   *
   * Runs on the real per-instance OBBs (build.obbs), not whole-piece bounding
   * spheres: a 3 × 3 × 0.2 m wall's sphere is 2.1 m across, so the old test let
   * a wall you were aiming BESIDE beat the post you were aiming AT, and ranked
   * by centre-projection rather than by where the ray actually enters. Boxes
   * are padded by PICK_PAD_M so hair-thin geometry (railings, ladder rungs) is
   * still hittable with a mathematical line.
   *
   * `preferPid` is a HYSTERESIS, not a lock: the previous pick only survives
   * while nothing else is meaningfully nearer (PICK_STICKY_M) — steadying the
   * highlight on a shared edge without ever hijacking a clear new aim.
   */
  pickPiece(beaconId: string, origin: [number, number, number], dir: [number, number, number],
    maxDist: number, preferPid?: number): { pid: number; dist: number; centre: [number, number, number] } | null {
    const build = this.builds.get(beaconId);
    if (!build) return null;
    const hit = rayHitObbs(build.obbs, origin[0], origin[1], origin[2],
      dir[0], dir[1], dir[2], maxDist, PICK_PAD_M);
    if (!hit) return null;
    let pid = hit.pid;
    let dist = hit.dist;
    if (preferPid !== undefined && preferPid !== pid) {
      // Is the previous pick still under the ray, and still competitive?
      const prev = build.obbs.filter((o) => o.pid === preferPid);
      const prevHit = prev.length
        ? rayHitObbs(prev, origin[0], origin[1], origin[2], dir[0], dir[1], dir[2], maxDist, PICK_PAD_M)
        : null;
      if (prevHit && prevHit.dist <= dist + PICK_STICKY_M) { pid = prevHit.pid; dist = prevHit.dist; }
    }
    const piece = build.pieces.find((p) => p.pid === pid);
    const b = piece ? this.boundsOf(piece) : null;
    return { pid, dist, centre: b ? b.centre : [origin[0] + dir[0] * dist, origin[1] + dir[1] * dist, origin[2] + dir[2] * dist] };
  }

  // ── demolish highlight: red shells over the aimed piece + its dependents ──
  private highlightGroup: THREE.Group | null = null;
  private highlightKey = "";
  // ADDITIVE blending: commutative, so the per-frame transparent re-sort can
  // never flip overlapping cascade boxes (the "flashing" artifact) — the
  // stacked shells just sum to a hotter red.
  private highlightMat = new THREE.MeshBasicMaterial({
    color: 0x8a2018, transparent: true, opacity: 0.55, depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  /** The AIMED piece burns hotter than the cascade it would drag down with it —
   *  "this one, and these follow" has to read at a glance. */
  private highlightPrimaryMat = new THREE.MeshBasicMaterial({
    color: 0xd8503a, transparent: true, opacity: 0.75, depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  /**
   * Show (or clear, pids=null) the would-be-removed set for a beacon, drawn as
   * the pieces' REAL collision boxes rather than one doc-bounds cube — a 45°
   * roof panel or a stair flight highlighted as its bounding cube covers half a
   * room and reads as the wrong piece entirely. `primaryPid` is the aimed piece
   * (hot); everything else is the support cascade (dim).
   */
  setHighlight(beaconId: string | null, pids: readonly number[] | null, primaryPid?: number): void {
    const key = beaconId && pids && pids.length > 0
      ? `${beaconId}:${primaryPid ?? -1}:${[...pids].sort().join(",")}` : "";
    if (key === this.highlightKey) return;
    this.highlightKey = key;
    if (this.highlightGroup) {
      this.highlightGroup.parent?.remove(this.highlightGroup);
      this.highlightGroup.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
      this.highlightGroup = null;
    }
    if (!key || !beaconId || !pids) return;
    const build = this.builds.get(beaconId);
    if (!build) return;
    const want = new Set(pids);
    const group = new THREE.Group();
    const pad = PICK_PAD_M * 0.5; // matches the pick's forgiveness, visibly
    for (const piece of build.pieces) {
      if (!want.has(piece.pid)) continue;
      const mat = piece.pid === primaryPid ? this.highlightPrimaryMat : this.highlightMat;
      const boxes = catalogObbs(piece.t);
      if (boxes.length === 0) continue; // round-only piece — nothing solid to shell
      for (const o of boxes) {
        const box = new THREE.Mesh(new THREE.BoxGeometry(
          o.h[0] * 2 + pad, o.h[1] * 2 + pad, o.h[2] * 2 + pad), mat);
        const local = rotP(piece.q, o.c[0], o.c[1], o.c[2]);
        box.position.set(local[0] + piece.p[0], local[1] + piece.p[1], local[2] + piece.p[2]);
        box.quaternion.set(piece.q[0], piece.q[1], piece.q[2], piece.q[3]);
        box.quaternion.multiply(new THREE.Quaternion(o.q[0], o.q[1], o.q[2], o.q[3]));
        group.add(box);
      }
    }
    build.group.add(group);
    this.highlightGroup = group;
  }

  private rebake(beaconId: string, planetId: string, pieces: PlacedPiece[]): void {
    // A build that was already promoted stays promoted — placing/demolishing a
    // piece while standing in it must not flash the hologram for a frame.
    const wasBaked = this.builds.get(beaconId)?.baked ?? false;
    this.drop(beaconId);
    const planet = this.system.planets.find((pl) => pl.def.id === planetId);
    if (!planet) return; // planet not in this system build — skip
    const group = new THREE.Group();
    planet.group.add(group);
    // Collision boxes + a coarse bound (centroid + max reach) for the prefilter —
    // derived from the PIECES themselves, so it never goes stale under expand.
    const obbs = pieces.flatMap((p) => pieceWorldObbs(p));
    const boundC: [number, number, number] = [0, 0, 0];
    let boundR = 0;
    if (pieces.length > 0) {
      for (const p of pieces) { boundC[0] += p.p[0]; boundC[1] += p.p[1]; boundC[2] += p.p[2]; }
      boundC[0] /= pieces.length; boundC[1] /= pieces.length; boundC[2] /= pieces.length;
      for (const p of pieces) {
        const dx = p.p[0] - boundC[0], dy = p.p[1] - boundC[1], dz = p.p[2] - boundC[2];
        boundR = Math.max(boundR, Math.sqrt(dx * dx + dy * dy + dz * dz));
      }
      boundR += 8; // largest piece reach + probe slack
    }
    const build: BeaconBuild = {
      planetId, pieces: [...pieces], group, obbs, rooms: solveHomesteadRooms(pieces), boundC, boundR,
      baked: false, proxy: null,
      // The login/entry dwell: a fresh (never-promoted) build holds its
      // hologram for the reveal beat even with the camera right on top of it.
      promoteAfter: performance.now() + REVEAL_DWELL_MS,
      lingerUntil: 0,
    };
    // The hologram shows IMMEDIATELY (one instanced draw, no geometry work);
    // update() promotes to the full bake when the camera is near, 1/frame.
    // Already-promoted builds re-bake in place (see wasBaked above).
    if (wasBaked && build.pieces.length > 0) {
      this.builds.set(beaconId, build);
      this.bakeFull(build);
      return;
    }
    build.proxy = this.makeProxy(build.pieces);
    if (build.proxy) group.add(build.proxy);
    this.builds.set(beaconId, build);
  }

  /** Highest walkable piece-floor radius under planet-local p (character roofStand hook). */
  standRadiusAt(planetId: string, x: number, y: number, z: number): number | null {
    let best: number | null = null;
    for (const build of this.builds.values()) {
      if (build.planetId !== planetId || build.obbs.length === 0) continue;
      const dx = x - build.boundC[0], dy = y - build.boundC[1], dz = z - build.boundC[2];
      if (dx * dx + dy * dy + dz * dz > build.boundR * build.boundR) continue;
      const r = standRadiusObbs(build.obbs, x, y, z);
      if (r !== null && (best === null || r > best)) best = r;
    }
    return best;
  }

  /** Enclosed player-build air for weather, camera, and ambient-light gates. */
  indoorsAt(planetId: string, x: number, y: number, z: number): boolean {
    for (const build of this.builds.values()) {
      if (build.planetId !== planetId || build.rooms.fields.length === 0) continue;
      const dx = x - build.boundC[0], dy = y - build.boundC[1], dz = z - build.boundC[2];
      if (dx * dx + dy * dy + dz * dz > build.boundR * build.boundR) continue;
      if (homesteadIndoorsAt(build.rooms, x, y, z)) return true;
    }
    return false;
  }

  /** Valid recognized room at a planet-local point. */
  roomAt(planetId: string, x: number, y: number, z: number): HomesteadRoom | null {
    for (const build of this.builds.values()) {
      if (build.planetId !== planetId || build.rooms.rooms.length === 0) continue;
      const dx = x - build.boundC[0], dy = y - build.boundC[1], dz = z - build.boundC[2];
      if (dx * dx + dy * dy + dz * dz > build.boundR * build.boundR) continue;
      const room = homesteadRoomAt(build.rooms, x, y, z);
      if (room) return room;
    }
    return null;
  }

  /** Copies of the recognized room summaries for UI/debug consumers. */
  roomsOf(beaconId: string): HomesteadRoom[] {
    const build = this.builds.get(beaconId);
    return build ? build.rooms.rooms.map((room) => ({
      ...room,
      centre: [...room.centre],
      doorPids: [...room.doorPids],
      furniturePids: [...room.furniturePids],
      functions: [...room.functions],
      functionAnchors: { ...room.functionAnchors },
      functionSlots: Object.fromEntries(Object.entries(room.functionSlots)
        .map(([fn, pids]) => [fn, [...pids]])) as HomesteadRoom["functionSlots"],
    })) : [];
  }

  /** Read-only resident simulation seam. The returned solution/piece records
   * are replaced wholesale on rebake; callers must never mutate them. */
  residentSiteOf(beaconId: string): {
    planetId: string;
    pieces: readonly PlacedPiece[];
    rooms: HomesteadRoomSolution;
  } | null {
    const build = this.builds.get(beaconId);
    return build ? { planetId: build.planetId, pieces: build.pieces, rooms: build.rooms } : null;
  }

  /** Lateral wall push-out vs placed pieces (character wall-solver hook,
   *  corrected-position contract). */
  resolveAt(planetId: string, x: number, y: number, z: number, radius: number,
    out: { x: number; y: number; z: number }): boolean {
    let moved = false;
    for (const build of this.builds.values()) {
      if (build.planetId !== planetId || build.obbs.length === 0) continue;
      const dx = x - build.boundC[0], dy = y - build.boundC[1], dz = z - build.boundC[2];
      if (dx * dx + dy * dy + dz * dz > build.boundR * build.boundR) continue;
      if (resolveObbs(build.obbs, x, y, z, radius, out)) {
        x = out.x; y = out.y; z = out.z; // chain across builds
        moved = true;
      }
    }
    if (moved) { out.x = x; out.y = y; out.z = z; }
    return moved;
  }

  /** Camera-boom occlusion vs placed pieces (planet.homesteadOccludes hook,
   *  interiors P1 — before this the boom clipped straight through builds). */
  occludesAt(planetId: string, x: number, y: number, z: number, radius: number): boolean {
    for (const build of this.builds.values()) {
      if (build.planetId !== planetId || build.obbs.length === 0) continue;
      const dx = x - build.boundC[0], dy = y - build.boundC[1], dz = z - build.boundC[2];
      const reach = build.boundR + radius;
      if (dx * dx + dy * dy + dz * dz > reach * reach) continue;
      if (occludesObbs(build.obbs, x, y, z, radius)) return true;
    }
    return false;
  }

  /** First bolt-crossing fraction vs placed pieces (planet.homesteadSegmentHit
   *  hook, interiors P1 — before this bolts passed through builds). −1 = clear
   *  (the hook's number contract; see planetHost.ts). */
  segmentHitAt(planetId: string, ax: number, ay: number, az: number,
    bx: number, by: number, bz: number): number {
    let best: number | null = null;
    for (const build of this.builds.values()) {
      if (build.planetId !== planetId || build.obbs.length === 0) continue;
      // Coarse: segment midpoint within bound + half segment length.
      const mx = (ax + bx) / 2 - build.boundC[0];
      const my = (ay + by) / 2 - build.boundC[1];
      const mz = (az + bz) / 2 - build.boundC[2];
      const half = Math.hypot(bx - ax, by - ay, bz - az) / 2;
      const reach = build.boundR + half;
      if (mx * mx + my * my + mz * mz > reach * reach) continue;
      const t = segmentHitObbs(build.obbs, ax, ay, az, bx, by, bz);
      if (t !== null && (best === null || t < best)) best = t;
    }
    return best ?? -1;
  }

  private drop(beaconId: string): void {
    const build = this.builds.get(beaconId);
    if (!build) return;
    // Detach shared-resource children FIRST — the proxy's geometry/material and
    // the highlight material are shared and must not hit the disposal traversal.
    if (build.proxy) {
      build.group.remove(build.proxy);
      build.proxy.dispose();
      build.proxy = null;
    }
    // Detach any highlight living under this build FIRST — its material is
    // shared across highlights and must not be disposed by the traversal below.
    if (this.highlightGroup && this.highlightGroup.parent === build.group) {
      build.group.remove(this.highlightGroup);
      this.highlightGroup.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
      this.highlightGroup = null;
      this.highlightKey = "";
    }
    build.group.parent?.remove(build.group);
    build.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        const mat = o.material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else mat.dispose();
      }
    });
    this.builds.delete(beaconId);
  }

  dispose(): void {
    for (const id of [...this.builds.keys()]) this.drop(id);
    this.highlightMat.dispose();
    this.highlightPrimaryMat.dispose();
    this.proxyGeo.dispose();
    this.proxyMat.dispose();
  }
}

/** v rotated by quaternion q (plain math — keeps call sites allocation-light). */
function rotP(q: [number, number, number, number], x: number, y: number, z: number): [number, number, number] {
  const [qx, qy, qz, qw] = q;
  // t = 2 q × v
  const tx = 2 * (qy * z - qz * y);
  const ty = 2 * (qz * x - qx * z);
  const tz = 2 * (qx * y - qy * x);
  // v' = v + qw t + q × t
  return [
    x + qw * tx + qy * tz - qz * ty,
    y + qw * ty + qz * tx - qx * tz,
    z + qw * tz + qx * ty - qy * tx,
  ];
}
